import React, { useState, useRef } from "react";
import {
  Building2,
  Mail,
  FileText,
  Shield,
  Database,
  Upload,
  Trash2,
  CheckCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { useApp } from "../../context/AppContext";
import { chapterSettingsService } from "../../services/chapterSettingsService";
import { Card } from "../ui/Card";

// ── Image Upload Field ──────────────────────────────────────────

const ImageUploadField = ({
  label,
  hint,
  currentUrl,
  onUpload,
  onRemove,
  palette,
  uploading,
  previewHeight = 64,
}) => {
  const fileInputRef = useRef(null);

  const labelStyle = {
    display: "block",
    fontSize: FONT_SIZES.xs,
    fontWeight: 700,
    color: palette.text2,
    marginBottom: 6,
  };

  const hintStyle = {
    fontSize: 11,
    color: palette.text3,
    marginTop: 6,
    lineHeight: 1.4,
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <label style={labelStyle}>{label}</label>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: 14,
          borderRadius: 10,
          border: `1px dashed ${palette.cardBorder}`,
          background: `${palette.accent1}04`,
        }}
      >
        {/* Preview / Placeholder */}
        {currentUrl ? (
          <img
            src={currentUrl}
            alt={label}
            style={{
              height: previewHeight,
              maxWidth: 160,
              objectFit: "contain",
              borderRadius: 6,
              border: `1px solid ${palette.cardBorder}`,
              background: "#fff",
              padding: 4,
            }}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        ) : (
          <div
            style={{
              width: previewHeight,
              height: previewHeight,
              borderRadius: 8,
              border: `2px dashed ${palette.cardBorder}`,
              background: palette.inputBg,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: palette.text3,
              flexShrink: 0,
            }}
          >
            <Upload size={ICON_SIZES.md} />
            <span style={{ fontSize: 9, marginTop: 2 }}>No file</span>
          </div>
        )}

        {/* Controls */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onUpload(file);
                e.target.value = "";
              }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "7px 14px",
                borderRadius: 7,
                border: "none",
                background: uploading ? palette.text3 : palette.accent1,
                color: "#fff",
                fontSize: FONT_SIZES.xs,
                fontWeight: 700,
                fontFamily: FONT_FAMILY,
                cursor: uploading ? "wait" : "pointer",
                opacity: uploading ? 0.7 : 1,
                whiteSpace: "nowrap",
              }}
            >
              <Upload size={ICON_SIZES.xs} />
              {uploading
                ? "Uploading..."
                : currentUrl
                ? "Replace"
                : "Upload Image"}
            </button>
            {currentUrl && !uploading && (
              <button
                onClick={onRemove}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "7px 12px",
                  borderRadius: 7,
                  border: "1px solid #fca5a5",
                  background: "#fef2f2",
                  color: "#991b1b",
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 600,
                  fontFamily: FONT_FAMILY,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                <Trash2 size={ICON_SIZES.xs} /> Remove
              </button>
            )}
          </div>
          <div style={hintStyle}>{hint}</div>
        </div>
      </div>
    </div>
  );
};

// ── Receipt Token Reference ─────────────────────────────────────

const ReceiptTokenReference = ({ palette }) => {
  const [open, setOpen] = useState(false);

  const tokens = [
    { token: "{donorFirstName}", desc: 'Donor\'s first name (or "Friend")' },
    { token: "{donorFullName}", desc: "Donor's credentialed full name" },
    { token: "{chapterName}", desc: "Organization legal name" },
    { token: "{dafOrg}", desc: "DAF sponsoring organization name" },
    {
      token: "{receiptNumber}",
      desc: "Receipt number (e.g. PCPA-2026-0001)",
    },
    { token: "{signatureName}", desc: "Signatory name from organization settings" },
    { token: "{giftDate}", desc: "Formatted gift date" },
    { token: "{giftAmount}", desc: "Formatted gift amount (e.g. $1,000.00)" },
  ];

  return (
    <div
      style={{
        marginTop: 12,
        borderRadius: 8,
        border: `1px solid ${palette.cardBorder}`,
        overflow: "hidden",
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          width: "100%",
          padding: "10px 14px",
          border: "none",
          background: `${palette.accent1}06`,
          color: palette.text1,
          fontSize: FONT_SIZES.xs,
          fontWeight: 700,
          fontFamily: FONT_FAMILY,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        {open ? <ChevronDown size={ICON_SIZES.sm} /> : <ChevronRight size={ICON_SIZES.sm} />}
        Available Tokens
      </button>
      {open && (
        <div style={{ padding: "10px 14px" }}>
          <div
            style={{
              fontSize: 11,
              color: palette.text3,
              marginBottom: 8,
              lineHeight: 1.4,
            }}
          >
            Type these anywhere in the fields above. They will be replaced with
            actual values when the receipt is generated.
          </div>
          {tokens.map((t) => (
            <div
              key={t.token}
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 10,
                padding: "4px 0",
                borderBottom: `1px solid ${palette.cardBorder}22`,
                fontSize: FONT_SIZES.xs,
              }}
            >
              <code
                style={{
                  background: `${palette.accent1}10`,
                  padding: "2px 6px",
                  borderRadius: 3,
                  fontSize: 11,
                  fontWeight: 600,
                  color: palette.accent1,
                  flexShrink: 0,
                }}
              >
                {t.token}
              </code>
              <span style={{ color: palette.text2 }}>{t.desc}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Receipt IRS Reference ───────────────────────────────────────

const ReceiptIrsReference = ({ palette }) => {
  const [open, setOpen] = useState(false);

  const irsBlocks = [
    {
      label: "Cash / Check / Credit Card (no G&S)",
      text: "No goods or services were provided in exchange for this contribution.",
    },
    {
      label: "Cash / Check / Credit Card (with G&S)",
      text: 'In connection with your gift of [amount], goods or services described as "[description]" with a fair market value of [FMV] were provided. The deductible portion of your contribution is [deductible].',
    },
    {
      label: "In-Kind Gift",
      text: "No goods or services were provided in exchange for this contribution. [Organization] did not assign a value to this gift. Please consult your tax advisor to determine the fair market value for deduction purposes.",
    },
    {
      label: "Stock / Securities",
      text: "No goods or services were provided in exchange for this contribution. No value has been assigned by [Organization]. The deductible amount is the fair market value of the securities on the date of transfer, as determined by the donor.",
    },
    {
      label: "Donor Advised Fund",
      text: "This letter acknowledges receipt of a grant from [sponsoring org] on behalf of a donor. This is not a tax receipt -- your charitable deduction was recorded at the time of your contribution to [sponsoring org].",
    },
  ];

  return (
    <div
      style={{
        marginTop: 12,
        borderRadius: 8,
        border: `1px solid ${palette.cardBorder}`,
        overflow: "hidden",
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          width: "100%",
          padding: "10px 14px",
          border: "none",
          background: "#fffbeb",
          color: "#92400e",
          fontSize: FONT_SIZES.xs,
          fontWeight: 700,
          fontFamily: FONT_FAMILY,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        {open ? <ChevronDown size={ICON_SIZES.sm} /> : <ChevronRight size={ICON_SIZES.sm} />}
        IRS Acknowledgment Language (Not Editable)
      </button>
      {open && (
        <div style={{ padding: "10px 14px" }}>
          <div
            style={{
              fontSize: 11,
              color: palette.text3,
              marginBottom: 10,
              lineHeight: 1.4,
              display: "flex",
              alignItems: "flex-start",
              gap: 6,
            }}
          >
            <AlertTriangle
              size={ICON_SIZES.sm}
              style={{ color: "#d97706", flexShrink: 0, marginTop: 1 }}
            />
            The following language is required by the IRS and prints
            automatically on every receipt based on gift type. It cannot be
            customized to ensure compliance.
          </div>
          {irsBlocks.map((block) => (
            <div
              key={block.label}
              style={{
                marginBottom: 10,
                padding: "8px 10px",
                borderRadius: 6,
                background: palette.inputBg,
                border: `1px solid ${palette.cardBorder}`,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: palette.text1,
                  marginBottom: 4,
                }}
              >
                {block.label}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: palette.text2,
                  lineHeight: 1.5,
                  fontStyle: "italic",
                }}
              >
                {block.text}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Chapter Settings Panel ──────────────────────────────────────

export const ChapterSettingsPanel = ({ palette }) => {
  const {
    chapterName,
    setChapterName,
    chapterSettings,
    setChapterSettings,
    isAdmin,
    showMessage,
  } = useApp();

  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingSignature, setUploadingSignature] = useState(false);

  const updateField = (field, value) => {
    setChapterSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = async (file) => {
    setUploadingLogo(true);
    try {
      const url = await chapterSettingsService.uploadLogo(file);
      updateField("logoUrl", url);
      showMessage("Logo uploaded successfully");
    } catch (err) {
      showMessage("Logo upload failed: " + err.message, "error");
    }
    setUploadingLogo(false);
  };

  const handleLogoRemove = async () => {
    try {
      await chapterSettingsService.removeFile(chapterSettings.logoUrl);
    } catch {
      // Non-critical — continue clearing the URL
    }
    updateField("logoUrl", null);
    showMessage("Logo removed");
  };

  const handleSignatureUpload = async (file) => {
    setUploadingSignature(true);
    try {
      const url = await chapterSettingsService.uploadSignature(file);
      updateField("signatureUrl", url);
      showMessage("Signature image uploaded");
    } catch (err) {
      showMessage("Signature upload failed: " + err.message, "error");
    }
    setUploadingSignature(false);
  };

  const handleSignatureRemove = async () => {
    try {
      await chapterSettingsService.removeFile(chapterSettings.signatureUrl);
    } catch {
      // Non-critical
    }
    updateField("signatureUrl", null);
    showMessage("Signature image removed");
  };

  const inputStyle = {
    padding: "10px 14px",
    borderRadius: 8,
    fontSize: FONT_SIZES.sm,
    fontFamily: FONT_FAMILY,
    border: `1px solid ${palette.inputBorder}`,
    background: palette.inputBg,
    color: palette.inputColor,
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block",
    fontSize: FONT_SIZES.xs,
    fontWeight: 700,
    color: palette.text2,
    marginBottom: 4,
  };

  const hintStyle = {
    fontSize: 11,
    color: palette.text3,
    marginTop: 2,
    lineHeight: 1.4,
  };

  const fieldRowStyle = {
    marginBottom: 14,
  };

  const SectionHeader = ({ icon: SI, title, subtitle }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: subtitle ? 4 : 16,
        paddingBottom: subtitle ? 0 : 12,
        borderBottom: subtitle ? "none" : `1px solid ${palette.cardBorder}`,
      }}
    >
      <SI size={ICON_SIZES.md} style={{ color: palette.accent1 }} />
      <div>
        <span
          style={{
            fontSize: FONT_SIZES.md,
            fontWeight: 600,
            color: palette.text1,
          }}
        >
          {title}
        </span>
        {subtitle && (
          <div
            style={{
              fontSize: FONT_SIZES.xs,
              color: palette.text3,
              marginTop: 1,
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );

  const requiredFields = [
    { key: "legalName", label: "Legal Name" },
    { key: "addressLine1", label: "Address" },
    { key: "cityStateZip", label: "City/State/ZIP" },
    { key: "ein", label: "EIN" },
    { key: "receiptPrefix", label: "Receipt Prefix" },
  ];
  const filledRequired = requiredFields.filter((f) =>
    (chapterSettings[f.key] || "").trim()
  ).length;
  const isReceiptReady = filledRequired === requiredFields.length;

  return (
    <>
      {/* Receipt Readiness Indicator */}
      <Card style={{ marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "4px 0",
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: isReceiptReady ? "#dcfce7" : "#fef3c7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {isReceiptReady ? (
              <CheckCircle size={ICON_SIZES.md} style={{ color: "#16a34a" }} />
            ) : (
              <AlertTriangle size={ICON_SIZES.md} style={{ color: "#d97706" }} />
            )}
          </div>
          <div>
            <div
              style={{
                fontSize: FONT_SIZES.sm,
                fontWeight: 700,
                color: palette.text1,
              }}
            >
              {isReceiptReady
                ? "Receipt Ready"
                : `${filledRequired} of ${requiredFields.length} Required Fields Complete`}
            </div>
            <div
              style={{
                fontSize: FONT_SIZES.xs,
                color: palette.text3,
                lineHeight: 1.4,
              }}
            >
              {isReceiptReady
                ? "All required fields are configured. Receipts can be generated."
                : `Missing: ${requiredFields
                    .filter((f) => !(chapterSettings[f.key] || "").trim())
                    .map((f) => f.label)
                    .join(", ")}`}
            </div>
          </div>
        </div>
      </Card>

      {/* Card 1: Organization Identity */}
      <Card style={{ marginBottom: 16 }}>
        <SectionHeader icon={Building2} title="Organization Identity" />
        <p style={{ fontSize: FONT_SIZES.xs, color: palette.text2, marginBottom: 16, lineHeight: 1.5 }}>
          The display name appears in the app header and footer. The legal name and EIN appear on tax receipts.
        </p>
        <div style={fieldRowStyle}>
          <label style={labelStyle}>Display Name</label>
          <input value={chapterName} onChange={(e) => setChapterName(e.target.value)} placeholder="e.g. PCPA" style={inputStyle} />
          <div style={hintStyle}>Shown in app header and footer across all views.</div>
        </div>
        <div style={fieldRowStyle}>
          <label style={labelStyle}>Legal Name <span style={{ color: "#dc2626" }}>*</span></label>
          <input value={chapterSettings.legalName || ""} onChange={(e) => updateField("legalName", e.target.value)} placeholder="e.g. Pacific Conservatory Theatre, Inc." style={inputStyle} />
          <div style={hintStyle}>Full legal name as it appears on IRS receipts and acknowledgment letters.</div>
        </div>
        <div style={fieldRowStyle}>
          <label style={labelStyle}>EIN / Tax ID <span style={{ color: "#dc2626" }}>*</span></label>
          <input value={chapterSettings.ein || ""} onChange={(e) => updateField("ein", e.target.value)} placeholder="e.g. 77-0123456" style={{ ...inputStyle, maxWidth: 200 }} />
          <div style={hintStyle}>Employer Identification Number. Format: XX-XXXXXXX.</div>
        </div>
      </Card>

      {/* Card 2: Mailing Address */}
      <Card style={{ marginBottom: 16 }}>
        <SectionHeader icon={Mail} title="Mailing Address" />
        <p style={{ fontSize: FONT_SIZES.xs, color: palette.text2, marginBottom: 16, lineHeight: 1.5 }}>
          Appears in the receipt header alongside the organization name and EIN.
        </p>
        <div style={fieldRowStyle}>
          <label style={labelStyle}>Address Line 1 <span style={{ color: "#dc2626" }}>*</span></label>
          <input value={chapterSettings.addressLine1 || ""} onChange={(e) => updateField("addressLine1", e.target.value)} placeholder="e.g. P.O. Box 1234" style={inputStyle} />
        </div>
        <div style={fieldRowStyle}>
          <label style={labelStyle}>Address Line 2</label>
          <input value={chapterSettings.addressLine2 || ""} onChange={(e) => updateField("addressLine2", e.target.value)} placeholder="Suite, building, etc. (optional)" style={inputStyle} />
        </div>
        <div style={fieldRowStyle}>
          <label style={labelStyle}>City, State ZIP <span style={{ color: "#dc2626" }}>*</span></label>
          <input value={chapterSettings.cityStateZip || ""} onChange={(e) => updateField("cityStateZip", e.target.value)} placeholder="e.g. San Luis Obispo, CA 93406" style={inputStyle} />
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div style={{ ...fieldRowStyle, flex: "1 1 200px" }}>
            <label style={labelStyle}>Phone</label>
            <input value={chapterSettings.phone || ""} onChange={(e) => updateField("phone", e.target.value)} placeholder="e.g. (805) 555-1234" style={inputStyle} />
          </div>
          <div style={{ ...fieldRowStyle, flex: "1 1 250px" }}>
            <label style={labelStyle}>Email</label>
            <input value={chapterSettings.email || ""} onChange={(e) => updateField("email", e.target.value)} placeholder="e.g. info@pcpa.org" type="email" style={inputStyle} />
          </div>
        </div>
      </Card>

      {/* Card 3: Receipt Configuration */}
      <Card style={{ marginBottom: 16 }}>
        <SectionHeader icon={FileText} title="Receipt Configuration" />
        <p style={{ fontSize: FONT_SIZES.xs, color: palette.text2, marginBottom: 16, lineHeight: 1.5 }}>
          Controls receipt numbering and footer text. The prefix is combined with the year and a sequential number (e.g. PCPA-2026-0001).
        </p>
        <div style={fieldRowStyle}>
          <label style={labelStyle}>Receipt Number Prefix <span style={{ color: "#dc2626" }}>*</span></label>
          <input value={chapterSettings.receiptPrefix || ""} onChange={(e) => updateField("receiptPrefix", e.target.value)} placeholder="e.g. PCPA" style={{ ...inputStyle, maxWidth: 200 }} />
          <div style={hintStyle}>Used in receipt numbers: {chapterSettings.receiptPrefix || "PREFIX"}-{new Date().getFullYear()}-0001</div>
        </div>
        <div style={fieldRowStyle}>
          <label style={labelStyle}>Fiscal Year Start Month</label>
          <select value={chapterSettings.fiscalYearStartMonth || 1} onChange={(e) => updateField("fiscalYearStartMonth", parseInt(e.target.value, 10))} style={inputStyle}>
            <option value={1}>January (Calendar Year)</option>
            <option value={2}>February</option>
            <option value={3}>March</option>
            <option value={4}>April</option>
            <option value={5}>May</option>
            <option value={6}>June</option>
            <option value={7}>July</option>
            <option value={8}>August</option>
            <option value={9}>September</option>
            <option value={10}>October</option>
            <option value={11}>November</option>
            <option value={12}>December</option>
          </select>
          <div style={hintStyle}>Sets the fiscal year for dashboard KPIs and reports. For example, if your fiscal year starts July 1, select July.</div>
        </div>
        <div style={fieldRowStyle}>
          <label style={labelStyle}>Fiscal Year Note</label>
          <input value={chapterSettings.fiscalYearNote || ""} onChange={(e) => updateField("fiscalYearNote", e.target.value)} placeholder="e.g. Tax year runs January 1 through December 31." style={inputStyle} />
          <div style={hintStyle}>Optional small-print note in the receipt footer.</div>
        </div>
      </Card>

      {/* Card 4: Signature Block */}
      <Card style={{ marginBottom: 16 }}>
        <SectionHeader icon={Shield} title="Signature Block" />
        <p style={{ fontSize: FONT_SIZES.xs, color: palette.text2, marginBottom: 16, lineHeight: 1.5 }}>
          The signature block appears at the bottom of every receipt. Upload images directly — accepted formats are PNG, JPG, WebP, and SVG (max 2 MB).
        </p>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div style={{ ...fieldRowStyle, flex: "1 1 250px" }}>
            <label style={labelStyle}>Signatory Name</label>
            <input value={chapterSettings.signatureName || ""} onChange={(e) => updateField("signatureName", e.target.value)} placeholder="e.g. Jane Smith" style={inputStyle} />
          </div>
          <div style={{ ...fieldRowStyle, flex: "1 1 250px" }}>
            <label style={labelStyle}>Signatory Title</label>
            <input value={chapterSettings.signatureTitle || ""} onChange={(e) => updateField("signatureTitle", e.target.value)} placeholder="e.g. Executive Director" style={inputStyle} />
          </div>
        </div>
        <ImageUploadField label="Organization Logo" hint="Appears in the receipt header next to the organization name. Recommended: transparent PNG, roughly 200×80 px." currentUrl={chapterSettings.logoUrl} onUpload={handleLogoUpload} onRemove={handleLogoRemove} palette={palette} uploading={uploadingLogo} previewHeight={60} />
        <ImageUploadField label="Signature Image" hint="Scanned signature displayed on receipts (~1.5″ wide). If not provided, a signature line is printed instead." currentUrl={chapterSettings.signatureUrl} onUpload={handleSignatureUpload} onRemove={handleSignatureRemove} palette={palette} uploading={uploadingSignature} previewHeight={48} />
      </Card>

      {/* Card 5: Receipt Language */}
      <Card style={{ marginBottom: 16 }}>
        <SectionHeader icon={FileText} title="Receipt Language" />
        <p style={{ fontSize: FONT_SIZES.xs, color: palette.text2, marginBottom: 16, lineHeight: 1.5 }}>
          Customize the text that appears on gift receipts and acknowledgment letters. Leave any field blank to use the default wording. Use{" "}
          <code style={{ background: `${palette.accent1}10`, padding: "1px 5px", borderRadius: 3, fontSize: 11 }}>{"{tokens}"}</code>{" "}
          to insert dynamic values — see the token reference below.
        </p>
        <div style={fieldRowStyle}>
          <label style={labelStyle}>Thank-You Paragraph</label>
          <textarea value={chapterSettings.receiptThankYou || ""} onChange={(e) => updateField("receiptThankYou", e.target.value)} placeholder="Thank you for your generous contribution to {chapterName}. Your support makes our work possible." rows={3} style={{ ...inputStyle, resize: "vertical" }} />
          <div style={hintStyle}>Appears after the salutation on all receipts except DAF.</div>
        </div>
        <div style={fieldRowStyle}>
          <label style={labelStyle}>DAF Thank-You Paragraph</label>
          <textarea value={chapterSettings.receiptThankYouDaf || ""} onChange={(e) => updateField("receiptThankYouDaf", e.target.value)} placeholder="Thank you for directing a grant from {dafOrg} to {chapterName}. We are grateful for your generous support." rows={3} style={{ ...inputStyle, resize: "vertical" }} />
          <div style={hintStyle}>Used only on Donor Advised Fund acknowledgment letters.</div>
        </div>
        <div style={fieldRowStyle}>
          <label style={labelStyle}>Closing Line</label>
          <input value={chapterSettings.receiptClosing || ""} onChange={(e) => updateField("receiptClosing", e.target.value)} placeholder="With gratitude," style={inputStyle} />
          <div style={hintStyle}>Appears above the signature block on every receipt.</div>
        </div>
        <div style={fieldRowStyle}>
          <label style={labelStyle}>DAF Notice Banner</label>
          <textarea value={chapterSettings.receiptDafNotice || ""} onChange={(e) => updateField("receiptDafNotice", e.target.value)} placeholder="Note: This is a gift acknowledgment letter, not a tax receipt. The donor received their tax receipt from the sponsoring organization at the time of their contribution." rows={2} style={{ ...inputStyle, resize: "vertical" }} />
          <div style={hintStyle}>Yellow banner shown at the top of DAF acknowledgment letters.</div>
        </div>
        <div style={{ borderTop: `1px solid ${palette.cardBorder}`, margin: "20px 0 16px" }} />
        <div style={fieldRowStyle}>
          <label style={labelStyle}>Email Subject Line</label>
          <input value={chapterSettings.receiptEmailSubject || ""} onChange={(e) => updateField("receiptEmailSubject", e.target.value)} placeholder="Gift Acknowledgment from {chapterName} -- {receiptNumber}" style={inputStyle} />
          <div style={hintStyle}>Subject line used when emailing a receipt to a donor.</div>
        </div>
        <div style={fieldRowStyle}>
          <label style={labelStyle}>Email Body</label>
          <textarea value={chapterSettings.receiptEmailBody || ""} onChange={(e) => updateField("receiptEmailBody", e.target.value)} placeholder={"Dear {donorFirstName},\n\nPlease find attached your gift receipt {receiptNumber} from {chapterName}.\n\nThank you for your generous support. Please retain this receipt for your tax records.\n\nWith gratitude,\n{signatureName}"} rows={5} style={{ ...inputStyle, resize: "vertical" }} />
          <div style={hintStyle}>Body text for emailed receipts. Use line breaks for formatting.</div>
        </div>
        <ReceiptTokenReference palette={palette} />
        <ReceiptIrsReference palette={palette} />
      </Card>

      {/* Auto-save note */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "10px 14px",
          borderRadius: 8,
          background: `${palette.accent1}06`,
          border: `1px solid ${palette.accent1}15`,
          fontSize: FONT_SIZES.xs,
          color: palette.text2,
        }}
      >
        <Database size={ICON_SIZES.sm} style={{ color: palette.accent1, flexShrink: 0 }} />
        All organization settings auto-save to Supabase as you type. Images upload to
        Supabase Storage automatically.
      </div>
    </>
  );
};
